import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ISO_4217_CODES,
  currenciesOfferedChanges,
  validateCurrenciesOffered,
  type CurrenciesOfferedSettings,
} from '@norvix/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { CURRENCIES_KEY, currenciesApi, type StoredCurrencies } from '../../api/currencies.api.js';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api.js';

import { SettingsHistoryButton, useSettingsLastChanged } from './SettingsHistory.js';
import { RestoreSaveBar } from './SettingsRestore.js';
import { SettingsSaveBar } from './SettingsSaveBar.js';
import { SettingsSectionHeader } from './SettingsSectionHeader.js';
import type { PendingChange } from './settings-changes.js';
import { useSectionRestore } from './useSettingsAccess.js';

/**
 * Settings → Organisation → Currencies offered (DEV-162).
 *
 * The currencies accounts are opened in, in the order every currency picker
 * shows them. Admin only. Every change needs a reason, kept in the History. A
 * currency that still has open accounts cannot be removed: the API refuses it
 * and names how many accounts each provider holds in it.
 */

/** The shortest reason the API accepts (currencies-settings.ts). */
const MIN_REASON = 10;
const SECTION = { group: 'organisation', id: 'currencies' } as const;

/** Moves the code at `index` by `step` (a new list). */
function moved(codes: readonly string[], index: number, step: -1 | 1): string[] {
  const target = index + step;
  if (target < 0 || target >= codes.length) return [...codes];
  const next = [...codes];
  const [code] = next.splice(index, 1);
  if (code !== undefined) next.splice(target, 0, code);
  return next;
}

function CurrenciesRestore({ stored, version, editing, onDone, onRestored }: {
  stored: StoredCurrencies; version: number; editing: boolean; onDone: () => void; onRestored: (saved: StoredCurrencies, version: number) => void;
}): JSX.Element | null {
  const preview = useQuery({
    queryKey: ['currencies-restore-preview', version, stored.version],
    queryFn: () => currenciesApi.preview(version, stored.version),
    retry: false,
  });
  const restore = useMutation({
    mutationFn: (reason: string) => currenciesApi.restore({ version, expectedVersion: stored.version, reason }),
    onSuccess: (saved) => onRestored(saved, version),
  });
  if (editing) {
    return <Alert severity="warning" sx={{ mt: 2 }} action={<Button onClick={onDone}>Cancel restore</Button>}>
      Save or discard your edits before restoring version {String(version)}.
    </Alert>;
  }
  if (preview.isLoading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (preview.isError || preview.data === undefined) {
    return <Alert severity="error" sx={{ mt: 2 }} action={<Button onClick={onDone}>Close</Button>}>
      {preview.error instanceof Error ? preview.error.message : `Version ${String(version)} cannot be restored.`}
    </Alert>;
  }
  return (
    <RestoreSaveBar
      plan={{ label: `version ${String(version)}`, changes: preview.data.changes }}
      saving={restore.isPending}
      {...(restore.isError ? { error: restore.error instanceof Error ? restore.error.message : 'The version could not be restored.' } : {})}
      onConfirm={(reason) => restore.mutate(reason)}
      onCancel={onDone}
    />
  );
}

function CurrenciesForm({ stored, restoreVersion, onRestoreDone }: {
  stored: StoredCurrencies; restoreVersion: number | null; onRestoreDone: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<readonly string[]>(stored.value.currencies);
  const [adding, setAdding] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);
  const [notice, setNotice] = useState('');
  const candidate: CurrenciesOfferedSettings = { currencies: codes };
  const checked = validateCurrenciesOffered(candidate);
  const changes: PendingChange[] = currenciesOfferedChanges(stored.value, checked.ok ? checked.value : candidate)
    .map((change) => ({ ...change, secret: false, needsReason: true }));
  const addable = ISO_4217_CODES.filter((code) => !codes.includes(code));

  const afterSave = async (saved: StoredCurrencies, message: string): Promise<void> => {
    queryClient.setQueryData(CURRENCIES_KEY, saved);
    setCodes(saved.value.currencies);
    setNotice(message);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: settingsHistoryKey('CURRENCIES') }),
      queryClient.invalidateQueries({ queryKey: ['account-providers'] }),
    ]);
  };

  const save = useMutation({
    onMutate: () => setNotice(''),
    mutationFn: (reason: string | undefined) => {
      if (!checked.ok) throw new Error(checked.error);
      return currenciesApi.save({ expectedVersion: stored.version, value: checked.value, reason: reason ?? '' });
    },
    onSuccess: async (saved) => {
      setSavedAt(Date.now());
      await afterSave(saved, 'Saved. Opening an account offers these currencies from now on; accounts already open are unchanged.');
    },
  });

  const set = (next: readonly string[]): void => {
    setNotice('');
    setCodes(next);
  };

  return (
    <Box>
      {notice !== '' ? <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Currencies, in display order</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each provider then opens only the ones it can hold: IBANQ the currencies on its own list, a shared account the
          currencies it has an account in (Settings → Treasury → Accounts), GuruPay’s vIBAN the currencies with a client-money account.
        </Typography>
        <List dense aria-label="Currencies offered">
          {codes.map((code, index) => (
            <ListItem
              key={code}
              divider
              secondaryAction={(
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Move up"><span>
                    <IconButton size="small" aria-label={`Move ${code} up`} disabled={index === 0} onClick={() => set(moved(codes, index, -1))}><ArrowUpwardIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                  <Tooltip title="Move down"><span>
                    <IconButton size="small" aria-label={`Move ${code} down`} disabled={index === codes.length - 1} onClick={() => set(moved(codes, index, 1))}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                  <Tooltip title="Remove"><span>
                    <IconButton size="small" aria-label={`Remove ${code}`} disabled={codes.length === 1} onClick={() => set(codes.filter((c) => c !== code))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                </Stack>
              )}
            >
              <ListItemText primary={code} secondary={index === 0 ? 'Shown first' : undefined} />
            </ListItem>
          ))}
        </List>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <TextField
            select
            size="small"
            label="Add a currency"
            value={adding ?? ''}
            onChange={(event) => setAdding(event.target.value === '' ? null : event.target.value)}
            sx={{ width: 220 }}
            helperText="Active ISO 4217 codes"
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
          >
            {addable.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
          </TextField>
          <Button variant="outlined" disabled={adding === null} onClick={() => { if (adding !== null) { set([...codes, adding]); setAdding(null); } }}>
            Add
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          A currency with open accounts cannot be removed until they are closed. Reports, statements and history of
          accounts in a currency no longer offered keep working.
        </Typography>
      </Paper>

      {restoreVersion !== null ? (
        <CurrenciesRestore
          stored={stored}
          version={restoreVersion}
          editing={changes.length > 0}
          onDone={onRestoreDone}
          onRestored={(saved, version) => {
            onRestoreDone();
            void afterSave(saved, `Version ${String(version)} restored as version ${String(saved.version)}.`);
          }}
        />
      ) : null}

      <SettingsSaveBar
        sticky
        changes={changes}
        saving={save.isPending}
        onSave={(reason) => save.mutate(reason)}
        onDiscard={() => { setCodes(stored.value.currencies); setNotice(''); }}
        reasonMinLength={MIN_REASON}
        reasonPrompt="This decides which currencies accounts can be opened in, so the reason is kept in the History."
        savedAt={savedAt}
        {...(checked.ok ? {} : { blocked: checked.error })}
        {...(save.isError ? { error: save.error instanceof Error ? save.error.message : 'The currencies could not be saved.' } : {})}
      />
    </Box>
  );
}

export function CurrenciesOfferedPage(): JSX.Element {
  const lastChanged = useSettingsLastChanged('CURRENCIES');
  const stored = useQuery({ queryKey: CURRENCIES_KEY, queryFn: () => currenciesApi.get() });
  const restore = useSectionRestore(SECTION);
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);

  return (
    <Box>
      <SettingsSectionHeader
        title="Currencies offered"
        lastChanged={lastChanged}
        actions={<SettingsHistoryButton subject="CURRENCIES" title="Currencies offered"
          {...(restore.canRestore ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreVersion(Number(entry.restoreTarget)) } : {})} />}
      >
        The currencies accounts are opened in, in the order every currency picker shows them. Every change asks for a
        reason and is kept in the History.
      </SettingsSectionHeader>
      {stored.isLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : null}
      {stored.isError ? (
        <Alert severity="error">{stored.error instanceof Error ? stored.error.message : 'Could not load the currencies offered.'}</Alert>
      ) : null}
      {stored.data !== undefined
        ? <CurrenciesForm stored={stored.data} restoreVersion={restoreVersion} onRestoreDone={() => setRestoreVersion(null)} />
        : null}
    </Box>
  );
}
